/**
 * Environment Configuration and Validation
 *
 * This module validates all required environment variables on app startup
 * and provides type-safe access to configuration values.
 */

// Optional environment variables with defaults
const OPTIONAL_ENV_VARS = {
  VITE_API_TIMEOUT: '30000',
  VITE_DEV_ALLOWED_EMAILS: ''
} as const;

// Validate that all required environment variables are present.
//
// IMPORTANT: every read uses DOT access with a literal property name
// (import.meta.env.VITE_SUPABASE_URL). Vite's static-analysis substitution
// at build time only handles dot access — bracket access with a runtime
// variable like import.meta.env[envVar] does NOT get inlined and reads
// undefined at runtime when the build provides env via process.env (e.g.
// Cloudflare Pages) instead of an .env file on disk.
export const validateEnvironment = (): void => {
  const missing: string[] = [];
  if (!import.meta.env.VITE_SUPABASE_URL) missing.push('VITE_SUPABASE_URL');
  if (!import.meta.env.VITE_SUPABASE_ANON_KEY) missing.push('VITE_SUPABASE_ANON_KEY');

  if (missing.length > 0) {
    const errorMessage =
      `Missing required environment variables: ${missing.join(', ')}\n\n` +
      'Please ensure these variables are set in your .env.local file:\n' +
      missing.map(var_ => `${var_}=your_value_here`).join('\n');

    throw new Error(errorMessage);
  }

  // Log API configuration status (helps debug production issues)
  console.log('[Environment] API Configuration:', {
    dataCollection: import.meta.env.VITE_DATA_COLLECTION_API_URL ? '✅ configured' : '❌ missing',
    vinProcessor: import.meta.env.VITE_VIN_PROCESSOR_API_URL ? '✅ configured' : '❌ missing',
    jobDispatcher: import.meta.env.VITE_JOB_DISPATCHER_API_URL ? '✅ configured' : '❌ missing',
    healthMonitoring: import.meta.env.VITE_HEALTH_MONITORING_API_URL
      ? '✅ configured'
      : '❌ missing',
    pipelineOrchestrator: import.meta.env.VITE_PIPELINE_ORCHESTRATOR_API_URL
      ? '✅ configured'
      : '❌ missing'
  });
};

// Type-safe environment configuration
export const env = {
  // Supabase configuration
  supabase: {
    url: import.meta.env.VITE_SUPABASE_URL as string,
    anonKey: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
    serviceRoleKey: import.meta.env.VITE_SUPABASE_SERVICE_ROLE_KEY as string | undefined
  },

  // Sibling app URLs
  adminUrl: import.meta.env.VITE_ADMIN_URL as string | undefined,

  // API configuration
  api: {
    timeout: parseInt(import.meta.env.VITE_API_TIMEOUT || OPTIONAL_ENV_VARS.VITE_API_TIMEOUT, 10),
    dataCollection: import.meta.env.VITE_DATA_COLLECTION_API_URL as string | undefined,
    vinProcessor: import.meta.env.VITE_VIN_PROCESSOR_API_URL as string | undefined,
    jobDispatcher: import.meta.env.VITE_JOB_DISPATCHER_API_URL as string | undefined,
    healthMonitoring: import.meta.env.VITE_HEALTH_MONITORING_API_URL as string | undefined,
    pipelineOrchestrator: import.meta.env.VITE_PIPELINE_ORCHESTRATOR_API_URL as string | undefined
  },

  // External services
  services: {
    resendApiKey: import.meta.env.RESEND_API_KEY as string | undefined,
    uptimeKumaUrl: import.meta.env.VITE_UPTIME_KUMA_URL as string | undefined
  },

  // Development configuration
  development: {
    allowedEmails: (
      import.meta.env.VITE_DEV_ALLOWED_EMAILS || OPTIONAL_ENV_VARS.VITE_DEV_ALLOWED_EMAILS
    )
      .split(',')
      .map(email => email.trim())
      .filter(email => email.length > 0),
    defaultEmail: import.meta.env.VITE_DEV_EMAIL as string | undefined,
    defaultPassword: import.meta.env.VITE_DEV_PASSWORD as string | undefined,
    defaultName: import.meta.env.VITE_DEV_NAME as string | undefined
  },

  // Routing configuration
  routing: {
    mapboxAccessToken: import.meta.env['VITE_MAPBOX_PUBLIC_TOKEN'] as string | undefined,
    googlePlacesApiKey: import.meta.env['VITE_GOOGLE_PLACES_API_KEY'] as string | undefined
  },

  // Google Analytics 4 configuration
  ga4: {
    measurementId: import.meta.env['VITE_GA4_MEASUREMENT_ID'] as string | undefined,
    enabled: import.meta.env['VITE_GA4_ENABLED'] === 'true',
    debugMode: import.meta.env['VITE_GA4_DEBUG_MODE'] === 'true'
  },

  // Runtime information
  isDevelopment: import.meta.env.DEV,
  isProduction: import.meta.env.PROD,
  mode: import.meta.env.MODE
} as const;

// validateEnvironment() is NOT called on module load anymore.
//
// Module-load side effects that throw are an anti-pattern in shared modules:
// when admin's bundle pulls in portal source transitively (via the auth
// context, security helpers, etc.), this module loads and runs immediately,
// and the throw kills admin even though admin has its own validator.
//
// Portal's main.tsx already calls validateEnvironment() explicitly during
// app startup (line 51). Admin's main.tsx does the same. Each app validates
// its own env at its own bootstrap point.

// Helper function to check if a service URL is configured
export const isServiceConfigured = (service: keyof typeof env.api): boolean => {
  const url = env.api[service];
  return typeof url === 'string' && url.length > 0;
};

// Helper function to get service URL with validation
export const getServiceUrl = (service: keyof typeof env.api): string => {
  const url = env.api[service];
  if (!url) {
    throw new Error(
      `${service} API URL is not configured.` +
        ' Please set VITE_' +
        `${service.toUpperCase().replace(/([A-Z])/g, '_$1')}` +
        '_API_URL in your environment.'
    );
  }
  return url;
};

// Export for logging (without sensitive values)
export const getEnvironmentInfo = () => ({
  mode: env.mode,
  isDevelopment: env.isDevelopment,
  isProduction: env.isProduction,
  configuredServices: {
    dataCollection: isServiceConfigured('dataCollection'),
    vinProcessor: isServiceConfigured('vinProcessor'),
    jobDispatcher: isServiceConfigured('jobDispatcher'),
    healthMonitoring: isServiceConfigured('healthMonitoring'),
    pipelineOrchestrator: isServiceConfigured('pipelineOrchestrator')
  },
  supabaseConfigured: Boolean(env.supabase.url && env.supabase.anonKey)
});
